'use client';

import type {
  VariantItem,
  ButtonDTO,
  SanityButton,
  SanityProductVariantItem,
  SanityProductVariantSection,
} from './product-variant.types';
import { Variants } from './ui';
import { mapItem, mapButton, mapVariantButton, normalizeBlock } from './lib';
import { getBackgroundClass } from '@workspace/lib';

type AnyBlock = { _type?: string; [key: string]: unknown };

type ProductVariantProps = {
  block: AnyBlock | SanityProductVariantSection;
  index: number;
};

export function ProductVariant({ block }: ProductVariantProps) {
  const normalizedBlock = normalizeBlock(block);

  // Check if the block is active
  if (normalizedBlock.isActive === false) {
    return null;
  }

  // Extract product variant items
  const rawItems: SanityProductVariantItem[] = Array.isArray(normalizedBlock.productVariantItems)
    ? normalizedBlock.productVariantItems
    : [];

  const items: VariantItem[] = rawItems.map(mapItem);

  // Extract buttons from block level
  const blockButtons: (SanityButton | undefined | null)[] = Array.isArray(normalizedBlock.buttons)
    ? normalizedBlock.buttons
    : normalizedBlock.button
      ? [normalizedBlock.button]
      : [];

  // Extract buttons from individual product variants
  const variantButtons = rawItems
    .map((item) => item.productVariant?.buttons)
    .filter((buttons): buttons is NonNullable<typeof buttons> => Array.isArray(buttons))
    .flat();

  // Combine all buttons and map them
  const allBlockButtons = blockButtons.map(mapButton).filter((btn): btn is ButtonDTO => btn !== undefined);

  const allVariantButtons = variantButtons.map(mapVariantButton).filter((btn): btn is ButtonDTO => btn !== undefined);

  const buttons: ButtonDTO[] = [...allBlockButtons, ...allVariantButtons];

  const backgroundClass = getBackgroundClass(normalizedBlock.backgroundColor);

  return (
    <Variants
      heading={normalizedBlock.heading ?? 'Product Variants'}
      items={items}
      buttons={buttons}
      compact={false}
      backgroundColor={backgroundClass}
    />
  );
}
